import React, { useState } from 'react';

// Shared pieces for the admin Analytics screens (overview and students).
// Plain divs, no chart package.

export type MoneyMap = Record<string, number>;
export interface AnalyticsData {
  generatedAt: string;
  rangeDays: number | null;
  funnel: { key: string; label: string; count: number }[];
  revenue: {
    currencies: string[];
    total: MoneyMap; inRange: MoneyMap; thisMonth: MoneyMap; lastMonth: MoneyMap;
    paymentsCount: number; newPayments: number; renewals: number;
    byPlan: { tier: string; payments: number; revenue: MoneyMap | number }[];
    monthly: { month: string; revenue: MoneyMap | number; payments: number }[];
    activeSubscribers: number; expiredSubscribers: number;
    recent: { email: string; tier: string; amount: number; currency: string; paidAt: string; channel: string; country: string }[];
  };
  growth: { weekly: { weekStart: string; signups: number; paid: number }[]; totalUsers: number; medianDaysToFirstPayment: number | null };
  targetExams: {
    exams: { key: string; label: string; students: number; paid: number; next30: number; next90: number; family: string; conversion: number }[];
    families: { family: string; students: number }[];
    freeQuestionUse: { paperId: string; label: string; guests: number; accounts: number; questionsAnswered: number; paidWithThisGoal: number }[];
    noGoal: number;
  };
  notes: { paymentsRecorded: number; practiceSource: string; guestsAllTime: number };
}

export const NAVY = '#0f1b4d';
export const TEAL = '#0E9594';

export const RANGES: { value: string; label: string }[] = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '12 months' },
  { value: 'all', label: 'All time' }
];

export const PLAN_NAMES: Record<string, string> = {
  basic: 'Basic', standard: 'Standard', extended: 'Extended', unlimited: 'Unlimited', all_access: 'All Access'
};

export function fmtMoney(map: MoneyMap | number | undefined, fallbackCurrency = 'USD'): string {
  if (map === undefined || map === null) return '0';
  if (typeof map === 'number') return formatOne(map, fallbackCurrency);
  const entries = Object.entries(map).filter(([, v]) => v);
  if (entries.length === 0) return formatOne(0, fallbackCurrency);
  return entries.map(([c, v]) => formatOne(v, c)).join(' + ');
}
export function formatOne(v: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: v % 1 === 0 ? 0 : 2 }).format(v);
  } catch {
    return `${currency} ${v.toLocaleString()}`;
  }
}
export function moneyValue(map: MoneyMap | number | undefined, currency: string): number {
  if (typeof map === 'number') return map;
  return (map && map[currency]) || 0;
}
export function pct(n: number, d: number): string {
  if (!d) return '0%';
  const v = (n / d) * 100;
  return `${v >= 10 || v === 0 ? Math.round(v) : v.toFixed(1)}%`;
}
export function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  if (!y || !m) return key;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
}
export function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export const Card: React.FC<{ title: string; icon?: React.ElementType; children: React.ReactNode; right?: React.ReactNode; className?: string }> = ({ title, icon: Icon, children, right, className = '' }) => (
  <section className={`min-w-0 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 ${className}`}>
    <div className="flex items-center justify-between gap-3 mb-4">
      <h2 className="flex items-center gap-2 text-[15px] font-extrabold text-slate-900">
        {Icon && <Icon className="w-4 h-4" style={{ color: TEAL }} />}
        {title}
      </h2>
      {right}
    </div>
    {children}
  </section>
);

export const Stat: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <div className="rounded-xl border border-slate-200 p-4 bg-white">
    <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-2xl font-extrabold text-slate-900 mt-1 tabular-nums">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

// Vertical bar chart: one series, thin bars with rounded tops, hover tooltip.
export const Columns: React.FC<{ items: { label: string; value: number; tip: string }[]; height?: number }> = ({ items, height = 150 }) => {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div>
      <div className="relative flex items-end gap-[2px]" style={{ height }}>
        {[0.5, 1].map((g) => (
          <div key={g} className="absolute left-0 right-0 border-t border-slate-100" style={{ bottom: `${g * 100}%` }} />
        ))}
        {items.map((it, i) => (
          <div
            key={i}
            className="relative flex-1 h-full flex items-end justify-center cursor-default"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          >
            <div
              className="w-full max-w-[28px] rounded-t transition-opacity"
              style={{ height: it.value > 0 ? `${Math.max(2, (it.value / max) * 100)}%` : 0, background: TEAL, opacity: hover === null || hover === i ? 1 : 0.45 }}
            />
            {hover === i && (
              <div className="absolute z-10 bottom-full mb-1 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-slate-900 text-white text-[11px] px-2.5 py-1.5 shadow-lg pointer-events-none">
                {it.tip}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-[2px] mt-1.5 border-t border-slate-300 pt-1">
        {items.map((it, i) => (
          <div key={i} className="flex-1 text-center text-[10px] text-slate-500 truncate">
            {items.length > 8 && i % 2 === 1 ? '' : it.label}
          </div>
        ))}
      </div>
    </div>
  );
};

// Horizontal bar used for the funnel and the demand lists.
export const HBar: React.FC<{ label: string; value: number; max: number; right: string; tip?: string; color?: string }> = ({ label, value, max, right, tip, color = TEAL }) => (
  <div className="group" title={tip}>
    <div className="flex items-baseline justify-between gap-3 text-[13px]">
      <span className="font-semibold text-slate-700 truncate">{label}</span>
      <span className="text-slate-900 font-bold tabular-nums whitespace-nowrap">{right}</span>
    </div>
    <div className="h-2.5 rounded-full bg-slate-100 mt-1 overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${max ? Math.max(value > 0 ? 1.5 : 0, (value / max) * 100) : 0}%`, background: color }} />
    </div>
  </div>
);

